import { useState, useEffect } from "react";
import type { Race } from "@/components/race-form";

// Storage keys
const STORAGE_KEYS = {
  RACES: "tritan-races",
  STEP: "tritan-step",
  SIZE: "tritan-size",
  EMAIL: "tritan-email",
};

export function useCustomizationState() {
  const [form, setForm] = useState<{
    step: number;
    races: Race[];
    size: string;
    email: string;
  }>({
    step: 1,
    races: [],
    size: "",
    email: "",
  });
  const [isLoaded, setIsLoaded] = useState(false);

  // Load data from localStorage on component mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        // Load races
        const savedRaces = localStorage.getItem(STORAGE_KEYS.RACES);
        if (savedRaces) {
          setForm((prevForm) => ({
            ...prevForm,
            races: JSON.parse(savedRaces),
          }));
        }

        // Load step
        const savedStep = localStorage.getItem(STORAGE_KEYS.STEP);
        if (savedStep) {
          setForm((prevForm) => ({
            ...prevForm,
            step: Number.parseInt(savedStep, 10),
          }));
        }

        // Load size
        const savedSize = localStorage.getItem(STORAGE_KEYS.SIZE);
        if (savedSize) {
          setForm((prevForm) => ({ ...prevForm, size: savedSize }));
        }

        // Load email
        const savedEmail = localStorage.getItem(STORAGE_KEYS.EMAIL);
        if (savedEmail) {
          setForm((prevForm) => ({ ...prevForm, email: savedEmail }));
        }
      } catch (error) {
        console.error("Error loading data from localStorage:", error);
      }

      setIsLoaded(true);
    }
  }, []);

  // Save to localStorage when values change
  useEffect(() => {
    if (isLoaded && typeof window !== "undefined") {
      try {
        localStorage.setItem(STORAGE_KEYS.RACES, JSON.stringify(form.races));
        localStorage.setItem(STORAGE_KEYS.STEP, form.step.toString());
        localStorage.setItem(STORAGE_KEYS.SIZE, form.size);
        localStorage.setItem(STORAGE_KEYS.EMAIL, form.email);
      } catch (error) {
        console.error("Error saving to localStorage:", error);
      }
    }
  }, [form, isLoaded]);

  // Form validation function
  const validateForm = (step?: number): string | null => {
    const currentStep = step || form.step;

    // Only validate email and size on step 1
    if (currentStep === 1) {
      if (!form.email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
        return "Please enter a valid email address";
      }
      if (!form.size) {
        return "Please select a size for your quarter zip";
      }
      return null;
    }

    // Validate races on step 2
    if (currentStep === 2) {
      if (!form.races || form.races.length === 0) {
        return "Please add at least one race";
      }
      return null;
    }

    // If we're at step 3 or beyond, validate everything
    if (!form.email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      return "Please enter a valid email address";
    }
    if (!form.size) {
      return "Please select a size for your quarter zip";
    }
    if (!form.races || form.races.length === 0) {
      return "Please add at least one race";
    }
    return null;
  };

  // Helper to update races
  const updateRaces = (updatedRaces: Race[]) => {
    setForm((prevForm) => ({ ...prevForm, races: updatedRaces }));
  };

  // Helper to update email
  const updateEmail = (email: string) => {
    setForm((prevForm) => ({ ...prevForm, email }));
  };

  // Helper to update size
  const updateSize = (size: string) => {
    setForm((prevForm) => ({ ...prevForm, size }));
  };

  return {
    form,
    setForm,
    isLoaded,
    validateForm,
    updateRaces,
    updateEmail,
    updateSize,
  };
}
